// src/components/ProtectedRoute.tsx
//
// ── FIX: The infinite redirect loop ("REDIRECT — wrong role, got: null")
//    was caused by redirecting BEFORE the auth state had finished loading.
//    Firebase auth is async — on first render `currentUser` is null and
//    `userRole` is null even for a logged-in admin, until the listener
//    resolves. Without checking `loading` first, the guard redirects to /
//    over and over as React re-renders.
//
//    Fix: return a loading spinner while `loading` is true.
//    Only redirect once loading === false.
//
import React from "react";
import { Navigate, Outlet } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { C } from "../theme";

// ── Role type — add "ranking_admin" alongside existing roles ─────────────────
type UserRole = "admin" | "coach" | "ranking_admin";

interface Props {
  allowedRoles: string[];
}

export default function ProtectedRoute({ allowedRoles }: Props) {
  const { currentUser, userRole, loading } = useAuth();

  // Cast to UserRole for typed comparisons (AuthContext exposes string | null)
  const role = userRole as UserRole | null;

  // ── 1. Still waiting for Firebase to resolve the session — do NOTHING yet.
  //       This is what was missing before. Without this check the component
  //       would immediately redirect because userRole === null.
  if (loading) {
    return (
      <div style={loadingStyle}>
        <img src="/geo.png" alt="Loading" style={{ width: 60, opacity: 0.8 }} />
      </div>
    );
  }

  // ── 2. Not logged in → go to landing / login
  if (!currentUser) {
    console.log("REDIRECT — not logged in");
    return <Navigate to="/login" replace />;
  }

  // ── 3. Logged in but wrong role (e.g. ranking_admin trying a coach route,
  //       or ranking_admin trying a sensitive admin-only route)
  if (!allowedRoles.includes(role ?? "")) {
    console.log("REDIRECT — wrong role, got:", role);
    // Both admin and ranking_admin land on the admin dashboard
    if (role === "admin" || role === "ranking_admin") {
      return <Navigate to="/admin" replace />;
    }
    // Coaches and any other role go to home
    return <Navigate to="/" replace />;
  }

  // ── 4. All good — render child routes
  return <Outlet />;
}

const loadingStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: "100vh",
  backgroundColor: C.red,
};